import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, ChevronDown, CheckCircle2, AlertCircle, Lightbulb } from 'lucide-react';
import TopMenu from '../../components/TopMenu';
import StatusBadge from '../../components/MockInterview/StatusBadge';
import { getAISessionHistory } from '../../services/mockInterviewService';
import { useNotification } from '../../notification/NotificationContext';
import { parseFeedback } from './parseFeedback';
import '../../components/MockInterview/MockInterview.css';
import './AIInterviewHistory.css';

/** Format date string in IST */
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return iso;
  }
};

const scoreColor = (score) => {
  if (score == null) return 'inherit';
  if (score >= 8) return '#22c55e';
  if (score >= 5) return '#f59e0b';
  return '#ef4444';
};

const AIInterviewHistory = ({ theme = 'dark', toggleTheme }) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const dark = theme === 'dark';

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    getAISessionHistory()
      .then((d) => setSessions(Array.isArray(d) ? d : []))
      .catch((e) => showNotification(e.message || 'Failed to load history', 'error'))
      .finally(() => setLoading(false));
  }, [showNotification]);

  if (loading) {
    return (
      <div className={`mi-page ${dark ? '' : 'mi-light'}`}>
        <TopMenu theme={theme} toggleTheme={toggleTheme} />
        <div className="mi-loading"><div className="mi-spin" /></div>
      </div>
    );
  }

  return (
    <div className={`mi-page ${dark ? '' : 'mi-light'}`}>
      <TopMenu theme={theme} toggleTheme={toggleTheme} />
      <div className="mi-container">
        <button className="mi-btn mi-btn-sm" onClick={() => navigate('/ai-interview')} style={{ marginBottom: 14 }}>
          <ArrowLeft size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Back
        </button>
        <p className="mi-kicker">Session History</p>
        <h1 className="mi-title">Your AI Interviews</h1>
        <p className="mi-subtitle">All past AI mock interview sessions with detailed feedback.</p>

        {sessions.length === 0 ? (
          <div className="mi-notice" style={{ marginTop: 18 }}>
            <h4>No history yet</h4>
            <p>Start your first AI interview and your session summary will show up here.</p>
            <button className="mi-btn mi-btn-primary" onClick={() => navigate('/ai-interview')}>
              <Play size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Start Interview
            </button>
          </div>
        ) : (
          <div className="mi-table-wrap">
            <table className="mi-table">
              <thead>
                <tr>
                  <th>Date (IST)</th>
                  <th>Duration</th>
                  <th>Score</th>
                  <th>Status</th>
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const isOpen = expandedId === s.id;
                  const scoreNum = s.score != null ? Number(s.score) : null;
                  const fb = parseFeedback(s.feedback);

                  return (
                    <React.Fragment key={s.id}>
                      <tr
                        onClick={() => setExpandedId(isOpen ? null : s.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{fmtDate(s.startedAt)}</td>
                        <td>{s.durationMinutes} min</td>
                        <td style={{ color: scoreColor(scoreNum), fontWeight: 700 }}>
                          {scoreNum != null ? `${scoreNum.toFixed(1)} / 10` : '—'}
                        </td>
                        <td><StatusBadge status={s.status} /></td>
                        <td style={{ textAlign: 'center' }}>
                          <ChevronDown
                            size={16}
                            style={{
                              transition: 'transform 0.2s ease',
                              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                              opacity: 0.7,
                            }}
                          />
                        </td>
                      </tr>

                      {isOpen && (
                        <tr className="mi-row-expand">
                          <td colSpan={5} style={{ padding: '18px 20px' }}>
                            {fb.summary || fb.strengths?.length || fb.weaknesses?.length ? (
                              <div className="aih-feedback">

                                {/* Summary */}
                                {fb.summary && (
                                  <p className="aih-summary">{fb.summary}</p>
                                )}

                                <div className="aih-grid">
                                  {/* Strengths */}
                                  {fb.strengths?.length > 0 && (
                                    <div className="aih-card aih-card--green">
                                      <div className="aih-card-head">
                                        <CheckCircle2 size={15} />
                                        <span>Strengths</span>
                                      </div>
                                      <ul className="aih-list">
                                        {fb.strengths.map((s, i) => (
                                          <li key={i}>{s}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Weaknesses */}
                                  {fb.weaknesses?.length > 0 && (
                                    <div className="aih-card aih-card--amber">
                                      <div className="aih-card-head">
                                        <AlertCircle size={15} />
                                        <span>Areas to Improve</span>
                                      </div>
                                      <ul className="aih-list">
                                        {fb.weaknesses.map((w, i) => (
                                          <li key={i}>{w}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Improvements */}
                                  {fb.improvements?.length > 0 && (
                                    <div className="aih-card aih-card--indigo">
                                      <div className="aih-card-head">
                                        <Lightbulb size={15} />
                                        <span>Action Items</span>
                                      </div>
                                      <ul className="aih-list">
                                        {fb.improvements.map((tip, i) => (
                                          <li key={i}>{tip}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <p style={{ margin: 0, opacity: 0.55, fontSize: '0.88rem' }}>
                                No feedback was recorded for this session.
                              </p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIInterviewHistory;
